import { isSupportedLanguage } from "@services/language.service";
import { Injectable, inject, resource, computed } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { firstValueFrom, filter, take, combineLatest } from "rxjs";
import { IframeMessagesService } from "@shared/iframe-messages-service";
import { ResponseErrorHandlerService } from "@shared/response-error-handler-service";
import { ITranslateObject, SupportedLanguage } from "@app/views-interfaces";
import { LanguageService } from "@services/index";
import { AuthService, type CurrentUser } from "@shared/auth";
import type { IFirmGroupsResponse, GroupMethod, IFirmGroupOptions, IFirmGroupOption, IFirmGroups, IFirmGroup } from "./ifirm-groups.interface";

@Injectable({ providedIn: "root" })
export class IFirmGroupsService {
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private readonly translateService = inject(TranslateService);
    private readonly languageService = inject(LanguageService);
    private readonly authService = inject(AuthService);
    private isReloading = true;
    public groupsCached: IFirmGroups | undefined;

    private groupsResource = resource<IFirmGroups | undefined, CurrentUser | null>({
        params: () => this.authService.currentUser(),
        loader: async ({ params: currentUser }) => {
            if (!currentUser) return Promise.resolve(undefined);
            if (!this.isReloading && this.groupsCached) return this.groupsCached;
            this.isReloading = false;
            this.groupsCached = await this.loadiFirmGroups();
            return this.groupsCached;
        },
    });
    private groupsResourceStatus$ = toObservable(this.groupsResource.status);
    public readonly groups = computed<IFirmGroups | undefined>(() => this.groupsResource.value());
    private readonly groups$ = toObservable(this.groups);

    public readonly group1 = computed<GroupMethod>(() => {
        const groups = this.groups();
        return (includeHidden = true) => groups?.group1Options?.filter((option) => includeHidden || option.IsVisible);
    });

    public readonly group1Title = computed<string>(() => {
        const langue = this.languageService.language();
        const group1 = this.groups()?.group1;
        return langue ? (group1?.[langue] ?? "") : "";
    });

    public readonly group1JobsEnabled = computed<boolean>(() => {
        const groups = this.groups();
        return (groups?.group1?.enabled && groups?.group1?.group1JobsEnabled) || false;
    });

    public readonly group1ContactEnabled = computed<boolean>(() => {
        const groups = this.groups();
        return (groups?.group1?.enabled && groups?.group1?.group1ContactsEnabled) || false;
    });

    public readonly group1Options = computed<IFirmGroupOptions>(() => {
        const options = this.groups()?.group1Options ?? [];
        return options;
    });

    public readonly group1OptionsById = computed<Map<number, IFirmGroupOption>>(() => {
        return new Map(this.group1Options().map((option) => [option.Id, option]));
    });

    public readonly group2 = computed<GroupMethod>(() => {
        const groups = this.groups();
        return (includeHidden = true) => groups?.group2Options?.filter((option) => includeHidden || option.IsVisible);
    });

    public readonly group2Title = computed<string>(() => {
        const langue = this.languageService.language();
        const group2 = this.groups()?.group2;
        return langue ? (group2?.[langue] ?? "") : "";
    });

    public readonly group2JobsEnabled = computed<boolean>(() => {
        const groups = this.groups();
        return (groups?.group2?.enabled && groups?.group2?.group2JobsEnabled) || false;
    });

    public readonly group2ContactEnabled = computed<boolean>(() => {
        const groups = this.groups();
        return (groups?.group2?.enabled && groups?.group2?.group2ContactsEnabled) || false;
    });

    public readonly group2Options = computed<IFirmGroupOptions>(() => {
        const options = this.groups()?.group2Options ?? [];
        return options;
    });

    public readonly group2OptionsById = computed<Map<number, IFirmGroupOption>>(() => {
        return new Map(this.group2Options().map((option) => [option.Id, option]));
    });

    public readonly isGroup1JobsEnabled = computed<boolean>(() => {
        const groups = this.groups();
        return (groups?.group1?.enabled && groups?.group1?.group1JobsEnabled) || false;
    });

    public readonly isGroup1ContactsEnabled = computed<boolean>(() => {
        const groups = this.groups();
        return (groups?.group1?.enabled && groups?.group1?.group1ContactsEnabled) || false;
    });

    public readonly isGroup2JobsEnabled = computed<boolean>(() => {
        const groups = this.groups();
        return (groups?.group2?.enabled && groups?.group2?.group2JobsEnabled) || false;
    });

    public readonly isGroup2ContactsEnabled = computed<boolean>(() => {
        const groups = this.groups();
        return (groups?.group2?.enabled && groups?.group2?.group2ContactsEnabled) || false;
    });

    public isGroupJobsEnabled(group: IFirmGroup): boolean {
        return (group?.enabled && group?.group1JobsEnabled) || false;
    }

    public isGroupContactsEnabled(group: IFirmGroup): boolean {
        return (group?.enabled && group?.group1ContactsEnabled) || false;
    }

    public reload(forceReload = false): void {
        if (this.authService.inReloginPrompted()) return;
        this.isReloading = forceReload;
        this.groupsResource.reload();
    }

    private async loadiFirmGroups(): Promise<IFirmGroups | undefined> {
        try {
            const result = await this.iframeMessagesService.postMessageResp<IFirmGroupsResponse>("getiFirmGroups", {}, "gotiFirmGroups");
            if (result.status === "success") {
                const groups = result.groups;
                groups?.group1Options.forEach((option) => {
                    if (option["fr-CA"] === "") {
                        option["fr-CA"] = option["en-CA"];
                    }
                    if (!option.IsVisible) {
                        option["fr-CA"] = this.translateService.instant("General.Inactive") + option["fr-CA"];
                        option["en-CA"] = this.translateService.instant("General.Inactive") + option["en-CA"];
                    }
                });
                groups?.group1Options.sort((a, b) =>
                    a[this.languageService.language() as keyof ITranslateObject].localeCompare(
                        b[this.languageService.language() as keyof ITranslateObject],
                    ),
                );
                groups?.group2Options.forEach((option) => {
                    if (option["fr-CA"] === "") {
                        option["fr-CA"] = option["en-CA"];
                    }
                    if (!option.IsVisible) {
                        option["fr-CA"] = this.translateService.instant("General.Inactive") + option["fr-CA"];
                        option["en-CA"] = this.translateService.instant("General.Inactive") + option["en-CA"];
                    }
                });
                groups?.group2Options.sort((a, b) =>
                    a[this.languageService.language() as keyof ITranslateObject].localeCompare(
                        b[this.languageService.language() as keyof ITranslateObject],
                    ),
                );
                return groups;
            }
        } catch (error) {
            console.error(error);
        }

        this.responseErrorHandlerService.showPopup({
            typeLayout: "ERROR",
            messageCode: "defaultError",
        });
        return;
    }

    public async getiFirmGroups(): Promise<IFirmGroups | undefined> {
        await this.waitForGroups();
        return this.groupsCached;
    }

    // Do not use it. Only use in getDashboardsList() for fallback compatibility. It will be removed in the future.
    private async waitForGroups(): Promise<void> {
        await firstValueFrom(
            combineLatest([this.groupsResourceStatus$, this.groups$]).pipe(
                filter(([status, groups]) => status !== "loading" && status !== "reloading" && groups !== undefined && groups !== null),
                take(1),
            ),
        );
    }

    public getGroup1Enabled(): boolean {
        return this.groups()?.group1?.enabled ?? false;
    }

    public getGroup2Enabled(): boolean {
        return this.groups()?.group2?.enabled ?? false;
    }

    public getGroup1UsersEnabled(): boolean | undefined {
        return this.groups()?.group1?.group1UsersEnabled;
    }

    public getGroup2UsersEnabled(): boolean | undefined {
        return this.groups()?.group2?.group2UsersEnabled;
    }

    public getGroup1ContactEnabled(): boolean {
        return this.groups()?.group1?.group1ContactsEnabled ?? false;
    }

    public getGroup2ContactEnabled(): boolean {
        return this.groups()?.group2?.group2ContactsEnabled ?? false;
    }

    public getGroup1JobsEnabled(): boolean {
        return this.groups()?.group1?.group1JobsEnabled ?? false;
    }

    public getGroup2JobsEnabled(): boolean {
        return this.groups()?.group2?.group2JobsEnabled ?? false;
    }

    public getGroup1Caption(language: SupportedLanguage): string {
        let title = (this.groups()?.group1 as any)?.[language];
        if (!title) title = this.groups()?.group1?.["en-CA"] ?? "";
        return title;
    }

    public getGroup2Caption(language: SupportedLanguage): string {
        let title = (this.groups()?.group2 as any)?.[language];
        if (!title) title = this.groups()?.group2?.["en-CA"] ?? "";
        return title;
    }

    public getGroup1Options(includeHidden = true): IFirmGroupOptions | undefined {
        return this.groups()?.group1Options.filter((option) => includeHidden || option.IsVisible);
    }

    public getGroup2Options(includeHidden = true): IFirmGroupOptions | undefined {
        return this.groups()?.group2Options.filter((option) => includeHidden || option.IsVisible);
    }

    public getGroupOptionCaption(groupObj: IFirmGroupOptions | undefined, id: number, language: string): string {
        if (groupObj) {
            for (const group of groupObj) {
                if (group.Id == id) return isSupportedLanguage(language) ? group[language] : "";
            }
        }
        return "";
    }

    public getGroup1OptionCaption(id: number, language: string): string {
        return this.getGroupOptionCaption(this.getGroup1Options(), id, language);
    }

    public getGroup2OptionCaption(id: number, language: string): string {
        return this.getGroupOptionCaption(this.getGroup2Options(), id, language);
    }
}
